import { motion } from 'framer-motion';
import { Eye, EyeOff, Hexagon, Loader2, Lock, User } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Modal from '../components/Modal';
import { useAuth } from '../contexts/AuthContext';

// Google Icon SVG Component
const GoogleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24">
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
);

const Login: React.FC = () => {
  const [usernameOrEmail, setUsernameOrEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isFormLoading, setIsFormLoading] = useState(false);
  const [isSignUp, setIsSignUp] = useState(false);
  const [modal, setModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'success' | 'error' | 'info' | 'warning';
  }>({
    isOpen: false,
    title: '',
    message: '',
    type: 'info',
  });

  const { signInWithUsernameOrEmail, signUpWithUsername, signInWithGoogle } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Show error from OAuth callback redirect
  useEffect(() => {
    const state = location.state as { error?: string } | null;
    if (state?.error) {
      setModal({
        isOpen: true,
        title: 'Sign In Failed',
        message: state.error,
        type: 'error',
      });
    }
  }, [location.state]);

  const handleGoogleSignIn = async () => {
    setIsGoogleLoading(true);
    try {
      await signInWithGoogle();
      // Navigation will happen via AuthCallback after OAuth redirect
    } catch (error: any) {
      setModal({
        isOpen: true,
        title: 'Sign In Error',
        message: error.message || 'Failed to sign in with Google. Please try again.',
        type: 'error',
      });
      setIsGoogleLoading(false);
    }
  };

  const handleEmailPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsFormLoading(true);

    try {
      if (isSignUp) {
        await signUpWithUsername(usernameOrEmail, password);
        setModal({
          isOpen: true,
          title: 'Account Created!',
          message: 'You can sign in now with your username and password.',
          type: 'success',
        });
        setIsSignUp(false);
        setUsernameOrEmail('');
        setPassword('');
      } else {
        await signInWithUsernameOrEmail(usernameOrEmail, password);
        navigate('/dashboard');
      }
    } catch (error: any) {
      setModal({
        isOpen: true,
        title: 'Authentication Error',
        message: error.message || 'An error occurred. Please try again.',
        type: 'error',
      });
    } finally {
      setIsFormLoading(false);
    }
  };

  return (
    <>
      <Modal
        isOpen={modal.isOpen}
        onClose={() => setModal({ ...modal, isOpen: false })}
        title={modal.title}
        message={modal.message}
        type={modal.type}
      />
      <div className='min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-50 to-slate-100'>
        <div className='max-w-md w-full space-y-8'>
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className='text-center'
          >
            <div className='w-20 h-20 mx-auto mb-6'>
              <div className='hexagon bg-gradient-to-r from-hexagon to-accent-500 flex items-center justify-center'>
                <Hexagon className='w-10 h-10 text-white' />
              </div>
            </div>
            <h2 className='text-3xl font-bold text-gray-900 mb-2'>
              Welcome to LuxLabz
            </h2>
            <p className='text-gray-600'>
              Sign in with Google or use your username/email and password
            </p>
          </motion.div>

          {/* Google Sign-In */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className='glass-effect rounded-2xl p-8'
          >
            <div className="text-center mb-6">
              <p className="text-gray-600 mb-6">
                Use your Google account to sign in and access your learning dashboard
              </p>
              
              <button
                type="button"
                onClick={handleGoogleSignIn}
                disabled={isGoogleLoading}
                className="w-full flex items-center justify-center space-x-3 px-6 py-4 border-2 border-gray-300 rounded-lg hover:border-gray-400 hover:bg-gray-50 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed bg-white shadow-lg hover:shadow-xl"
              >
                {isGoogleLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span className="font-medium text-gray-700">Signing in...</span>
                  </>
                ) : (
                  <>
                    <GoogleIcon />
                    <span className="font-medium text-gray-700">
                      Continue with Google
                    </span>
                  </>
                )}
              </button>
            </div>

            {/* Username/Email + Password Form */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">Or use username/email and password</span>
              </div>
            </div>

            <form className='space-y-6' onSubmit={handleEmailPasswordSubmit}>
              <div>
                <label
                  htmlFor='usernameOrEmail'
                  className='block text-sm font-medium text-gray-700 mb-2'
                >
                  {isSignUp ? 'Username' : 'Username or Email'}
                </label>
                <div className='relative'>
                  <div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
                    <User className='h-5 w-5 text-gray-400' />
                  </div>
                  <input
                    id='usernameOrEmail'
                    name='usernameOrEmail'
                    type='text'
                    autoComplete={isSignUp ? 'username' : 'username'}
                    required
                    value={usernameOrEmail}
                    onChange={e => setUsernameOrEmail(e.target.value)}
                    className='block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-hexagon focus:border-transparent transition-all duration-200'
                    placeholder={isSignUp ? 'Choose a username' : 'Enter username or email'}
                  />
                </div>
                {isSignUp && (
                  <p className='mt-1 text-xs text-gray-500'>
                    Letters, numbers, underscores and hyphens only. 2–50 characters.
                  </p>
                )}
              </div>

                  <div>
                    <label
                      htmlFor='password'
                      className='block text-sm font-medium text-gray-700 mb-2'
                    >
                      Password
                    </label>
                    <div className='relative'>
                      <div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
                        <Lock className='h-5 w-5 text-gray-400' />
                      </div>
                      <input
                        id='password'
                        name='password'
                        type={showPassword ? 'text' : 'password'}
                        autoComplete='current-password'
                        required
                        value={password}
                        onChange={e => setPassword(e.target.value)}
                        className='block w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-hexagon focus:border-transparent transition-all duration-200'
                        placeholder='Enter your password'
                      />
                      <button
                        type='button'
                        className='absolute inset-y-0 right-0 pr-3 flex items-center'
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? (
                          <EyeOff className='h-5 w-5 text-gray-400 hover:text-gray-600' />
                        ) : (
                          <Eye className='h-5 w-5 text-gray-400 hover:text-gray-600' />
                        )}
                      </button>
                    </div>
                  </div>

                  <button
                    type='submit'
                    disabled={isFormLoading}
                    className='w-full hexagon-button flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed'
                  >
                    {isFormLoading ? (
                      <>
                        <Loader2 className='w-5 h-5 animate-spin' />
                        <span>
                          {isSignUp ? 'Creating Account...' : 'Signing In...'}
                        </span>
                      </>
                    ) : (
                      <span>{isSignUp ? 'Create Account' : 'Sign In'}</span>
                    )}
                  </button>
                </form>

            <div className='mt-6 space-y-3'>
                  <div className='text-center'>
                    <button
                      type='button'
                      onClick={() => setIsSignUp(!isSignUp)}
                      className='text-hexagon hover:text-hexagon-dark text-sm font-medium transition-colors duration-200'
                    >
                      {isSignUp
                        ? 'Already have an account? Sign in'
                        : "Don't have an account? Sign up"}
                    </button>
                  </div>
                  {!isSignUp && (
                    <div className='text-center'>
                      <Link
                        to='/reset-password'
                        className='text-gray-600 hover:text-hexagon text-sm font-medium transition-colors duration-200'
                      >
                        Forgot your password?
                      </Link>
                    </div>
                  )}
            </div>

            <div className="mt-6 pt-6 border-t border-gray-200">
              <p className="text-xs text-gray-500 text-center">
                By signing in, you agree to our Terms of Service and Privacy Policy.
                <br />
                Your Google account information is used only for authentication.
              </p>
            </div>
          </motion.div>

          {/* Back to Home */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className='text-center'
          >
            <Link
              to='/'
              className='text-gray-600 hover:text-hexagon text-sm transition-colors duration-200'
            >
              ← Back to Home
            </Link>
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default Login;
